import React, { useMemo, useState } from 'react';
import BatchDetailsModal from './BatchDetailsModal';

function StationBatchDetailsModal({ station, statusFilter, batchData, onClose, onBack }) {
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [showBatchDetails, setShowBatchDetails] = useState(false);

  // Helper function to format dates
  const formatDate = (date) => {
    if (!date) return '';

    // Handle Firebase Timestamp objects
    if (date && typeof date === 'object' && date.seconds) {
      const jsDate = new Date(date.seconds * 1000);
      return jsDate.toLocaleDateString('en-US');
    }

    // Handle regular Date objects
    if (date instanceof Date) {
      return date.toLocaleDateString('en-US');
    }

    // Handle string dates
    if (typeof date === 'string') {
      return date;
    }

    return '';
  };

  // Filter batches based on the selected status
  const filteredBatches = useMemo(() => {
    if (!batchData || !station) return [];

    return batchData.filter((batch) => {
      const location = batch.Location ? String(batch.Location).split(/-/)[0] : '';
      const stationNameToUse = station.shortName || station.name;
      if (
        !(
          stationNameToUse.toLowerCase().includes(location.toLowerCase()) ||
          location.toLowerCase().includes(stationNameToUse.toLowerCase())
        ) ||
        location === ''
      ) {
        return false;
      }

      if (statusFilter === 'all') return true;
      return batch.Status === statusFilter;
    });
  }, [batchData, station, statusFilter]);

  // Get status display text
  const getStatusTitle = () => {
    switch (statusFilter) {
      case 'On Specification':
        return 'On Specification Batches';
      case 'Needs Attention':
        return 'Needs Attention Batches';
      case 'Opportunity':
        return 'Opportunity Batches';
      case 'all':
        return 'All Batches';
      default:
        return 'Batches';
    }
  };

  // Determine row color based on status
  const getRowColor = (status) => {
    switch (status) {
      case 'On Specification':
        return '#90ee90';
      case 'Needs Attention':
        return '#ffff99';
      case 'Opportunity':
        return '#87ceeb';
      default:
        return '#ffffff';
    }
  };

  const handleBatchClick = (batch) => {
    setSelectedBatch(batch);
    setShowBatchDetails(true);
  };

  const handleBackFromBatchDetails = () => {
    setShowBatchDetails(false);
    setSelectedBatch(null);
  };

  // Generate dynamic instrument prefix based on station name
  const instrumentPrefix = station?.name ? station.name.substring(0, 3).toUpperCase() : 'OAK';

  // If showing batch details, render the BatchDetailsModal
  if (showBatchDetails && selectedBatch) {
    return (
      <BatchDetailsModal
        batch={selectedBatch}
        onClose={onClose}
        onBack={handleBackFromBatchDetails}
      />
    );
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="station-batch-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{station.name} Dashboard</h2>
          <div className="header-buttons">
            <button className="back-button" onClick={onBack}>
              ← Back
            </button>
            <button className="close-button" onClick={onClose}>
              ×
            </button>
          </div>
        </div>

        <div className="modal-body">
          {/* First Table - Batch Status */}
          <div className="table-section">
            <h3>Batch Status - {getStatusTitle()}</h3>
            <div className="table-wrapper">
              <table className="batch-status-table">
                <thead>
                  <tr>
                    <th>Batch ID</th>
                    <th>Group ID</th>
                    <th>Date Received</th>
                    <th>Customer</th>
                    <th>Product</th>
                    <th>BOL Volume (gal)</th>
                    <th>Metered Volume (gal)</th>
                    <th>Sulfur (mg/kg)</th>
                    <th>RVP (psi)</th>
                    <th>Benzene (ppm)</th>
                    <th>Click to view</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBatches.length > 0 ? (
                    filteredBatches.map((batch, index) => (
                      <tr
                        key={batch.id || index}
                        style={{ backgroundColor: getRowColor(batch.Status) }}
                      >
                        <td>{batch.batchid || batch.id || `rh-24-${3132 + index}`}</td>
                        <td>{batch.groupid || ''}</td>
                        <td>{formatDate(batch['Date Received']) || '3/1/26'}</td>
                        <td>{batch.Customer || 'Sample Customer'}</td>
                        <td>{batch.Product || 'Diesel'}</td>
                        <td>{batch['BOL Volume'] || (4925 + index * 100).toLocaleString()}</td>
                        <td>{batch['Metered Volume'] || (4925 + index * 100).toLocaleString()}</td>
                        <td>{batch.sulfur || 10 - index}</td>
                        <td>
                          {batch.rvp || (index % 2 === 0 ? 'NA' : (8.5 + index * 0.5).toFixed(1))}
                        </td>
                        <td>{batch.benzene || (index * 0.25).toFixed(2)}</td>
                        <td>
                          <button className="click-button" onClick={() => handleBatchClick(batch)}>
                            Click
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="11" className="no-data">
                        No batches found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Second Table - Analytical Asset Status */}
          <div className="table-section">
            <h3>Analytical Asset Status</h3>
            <div className="table-wrapper">
              <table className="analytical-status-table">
                <thead>
                  <tr>
                    <th>Instrument ID</th>
                    <th>Last SQC sample</th>
                    <th>Last Daily Check</th>
                    <th>SQC status</th>
                    <th>Daily Check status</th>
                    <th>View SQC</th>
                    <th>View Instrument Manager</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ backgroundColor: '#90ee90' }}>
                    <td>{instrumentPrefix}-RVP-1</td>
                    <td>03/01/2026</td>
                    <td>03/01/2026</td>
                    <td className="status-pass">PASS</td>
                    <td className="status-pass">PASS</td>
                    <td>
                      <button className="click-button">Click</button>
                    </td>
                    <td>
                      <button className="click-button">Click</button>
                    </td>
                  </tr>
                  <tr style={{ backgroundColor: '#ffb3b3' }}>
                    <td>{instrumentPrefix}-RVP-2</td>
                    <td>03/01/2026</td>
                    <td>03/01/2026</td>
                    <td className="status-fail">FAIL</td>
                    <td className="status-pass">PASS</td>
                    <td>
                      <button className="click-button">Click</button>
                    </td>
                    <td>
                      <button className="click-button">Click</button>
                    </td>
                  </tr>
                  <tr style={{ backgroundColor: '#90ee90' }}>
                    <td>{instrumentPrefix}-Sulfur-1</td>
                    <td>03/01/2026</td>
                    <td>02/28/2026</td>
                    <td className="status-pass">PASS</td>
                    <td className="status-pass">PASS</td>
                    <td>
                      <button className="click-button">Click</button>
                    </td>
                    <td>
                      <button className="click-button">Click</button>
                    </td>
                  </tr>
                  <tr style={{ backgroundColor: '#d3d3d3' }}>
                    <td>{instrumentPrefix}-Sulfur-2</td>
                    <td>01/01/2026</td>
                    <td>01/01/2026</td>
                    <td className="status-oos">Out of Service</td>
                    <td className="status-oos">Out of Service</td>
                    <td>
                      <button className="click-button">Click</button>
                    </td>
                    <td>
                      <button className="click-button">Click</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Third Table - Workflow Status */}
          <div className="table-section">
            <h3>Workflow Status</h3>
            <div className="table-wrapper">
              <table className="workflow-status-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Workflow</th>
                    <th>Date Initiated</th>
                    <th>Batch ID</th>
                    <th>Customer</th>
                    <th>Product</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ backgroundColor: '#ffff99' }}>
                    <td>Analytical</td>
                    <td>SQC - retest</td>
                    <td>2/28/26</td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td className="status-pending">Pending</td>
                  </tr>
                  <tr>
                    <td>Batch</td>
                    <td>Request analytical, in-house</td>
                    <td>3/1/26</td>
                    <td>rh-24-3132</td>
                    <td>Exxon Baton Rouge</td>
                    <td>Diesel</td>
                    <td className="status-complete">Complete</td>
                  </tr>
                  <tr>
                    <td>Batch</td>
                    <td>Request analytical, 3rd party</td>
                    <td>3/2/26</td>
                    <td>rh-24-3129</td>
                    <td>Acme Refining</td>
                    <td>Gasoline</td>
                    <td className="status-complete">Complete</td>
                  </tr>
                  <tr style={{ backgroundColor: '#ffff99' }}>
                    <td>Batch</td>
                    <td>Waiver approval</td>
                    <td>3/2/26</td>
                    <td>rh-24-3129</td>
                    <td>Acme Refining</td>
                    <td>Gasoline</td>
                    <td className="status-pending">Pending</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StationBatchDetailsModal;
